import React, { useState } from 'react';
import { FiFileText, FiAlertTriangle, FiPlus } from 'react-icons/fi';
import { createReport } from '../../utils/sentimentApi';

const REPORT_TYPES = [
  { value: 'weekly_summary',  label: 'Weekly Summary' },
  { value: 'monthly_review',  label: 'Monthly Review' },
  { value: 'churn_risk',      label: 'Churn Risk Report' },
  { value: 'p1_escalation',   label: 'P1 Escalation Report' },
];

const DEFAULT_FORM = {
  subject:      '',
  report_type:  'weekly_summary',
  schedule_cron: '0 8 * * 1',
  recipients:   '',
  body_preview: '',
  top_action:   '',
};

export default function SentimentReports({ showToast }) {
  const [form, setForm]           = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [created, setCreated]     = useState([]);
  const [showForm, setShowForm]   = useState(false);

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const recipients = form.recipients.split(',').map(r => r.trim()).filter(Boolean);
    if (!form.subject.trim() || recipients.length === 0) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await createReport({ ...form, recipients });
      setCreated(prev => [result, ...prev]);
      setForm(DEFAULT_FORM);
      setShowForm(false);
      showToast?.('Report scheduled', 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div>
      <div className="sm-section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiFileText size={18} color="var(--sm-primary)" />
          <h2 className="sm-section-title">Scheduled Reports</h2>
        </div>
        <button className="sm-btn sm-btn-primary" onClick={() => setShowForm(v => !v)} style={{ gap: 6 }}>
          <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Report'}
        </button>
      </div>

      {error && <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>}

      {showForm && (
        <div className="sm-card" style={{ marginBottom: 24 }}>
          <form onSubmit={handleSubmit}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 16px' }}>Create Scheduled Report</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="sm-field">
                <label>Subject *</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={e => handleChange('subject', e.target.value)}
                  placeholder="e.g. Weekly Sentiment Summary"
                  required
                />
              </div>
              <div className="sm-field">
                <label>Report Type</label>
                <select className="sm-select" style={{ width: '100%' }} value={form.report_type} onChange={e => handleChange('report_type', e.target.value)}>
                  {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="sm-field">
                <label>Schedule (cron expression)</label>
                <input
                  type="text"
                  value={form.schedule_cron}
                  onChange={e => handleChange('schedule_cron', e.target.value)}
                  placeholder="0 8 * * 1  (Mon 8am)"
                />
              </div>
              <div className="sm-field">
                <label>Recipients * (comma-separated emails)</label>
                <input
                  type="text"
                  value={form.recipients}
                  onChange={e => handleChange('recipients', e.target.value)}
                  placeholder="alice@co.com, bob@co.com"
                  required
                />
              </div>
              <div className="sm-field" style={{ gridColumn: '1 / -1' }}>
                <label>Body Preview</label>
                <input
                  type="text"
                  value={form.body_preview}
                  onChange={e => handleChange('body_preview', e.target.value)}
                  placeholder="Top issues this week: …"
                />
              </div>
              <div className="sm-field" style={{ gridColumn: '1 / -1' }}>
                <label>Top Action / Recommendation</label>
                <input
                  type="text"
                  value={form.top_action}
                  onChange={e => handleChange('top_action', e.target.value)}
                  placeholder="e.g. Escalate logistics SLA breach to operations"
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
              <button type="button" className="sm-btn sm-btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="sm-btn sm-btn-primary" disabled={submitting}>
                {submitting ? 'Scheduling…' : 'Schedule Report'}
              </button>
            </div>
          </form>
        </div>
      )}

      {created.length === 0 ? (
        <div className="sm-empty">
          <div className="sm-empty-icon">📅</div>
          <span>No reports scheduled yet. Create one above.</span>
        </div>
      ) : (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Type</th>
                <th>Schedule</th>
                <th>Recipients</th>
                <th>Top Action</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {created.map((r, i) => (
                <tr key={r.report_id || i} style={{ '--row-delay': `${i * 30}ms` }}>
                  <td style={{ fontWeight: 600 }}>{r.subject}</td>
                  <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{r.report_type?.replace(/_/g, ' ')}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.schedule_cron}</td>
                  <td style={{ fontSize: 12 }}>
                    {(r.recipients || []).map((e, j) => <div key={j}>{e}</div>)}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)', maxWidth: 220 }}>{r.top_action}</td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
